import * as React from 'react';
import { connect } from 'react-redux';
import type { FieldFormProps, AttachmentFile } from '@helpcenter/field-types';
import { saveAttachment } from 'rest/attachments';
import type { MediaApiUploadToken } from 'rest/portal';
import { getUploadCredentials } from 'rest/portal-media';
import type { State } from 'state';
import { fetchPortalUploadMediaTokenAction } from 'state/actions/portal';
import {
	getMediaApiUploadToken as getPortalMediaApiUploadToken,
	getMediaApiError as getPortalMediaApiError,
} from 'state/selectors/portal';
import { UnSupportedBrowserErrorBoundary } from 'view/error-boundary';
import type { Auth } from '@atlaskit/media-core';
import { RequestCreateAttachmentFieldDumb } from './request-create-attachment-field-dumb';
import { RequestCreateAttachmentFieldError } from './request-create-attachment-field-error';

export interface Props extends FieldFormProps<AttachmentFile[]> {
	portalId: number;
}

export const MEDIA_TOKEN_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

interface StateProps {
	mediaApiUploadToken: MediaApiUploadToken | undefined;
	mediaApiError: string | undefined;
}

interface DispatchProps {
	fetchPortalUploadMediaToken: typeof fetchPortalUploadMediaTokenAction;
}

class RequestCreateAttachmentField extends React.PureComponent<Props & StateProps & DispatchProps> {
	private timeout: NodeJS.Timeout | undefined = undefined;
	private mediaUploadAuth: Auth | undefined = undefined;

	authProvider = async (): Promise<Auth> => {
		if (this.mediaUploadAuth) {
			return this.mediaUploadAuth;
		}

		const { portalId } = this.props;
		const { clientId, token, endpointUrl: baseUrl } = await getUploadCredentials(portalId);

		this.mediaUploadAuth = { clientId, token, baseUrl };
		// Ideally token should be expired based on expiryEpoch on the response from getUploadCredentials
		// The token expires in 20 minutes
		this.timeout = setTimeout(() => {
			this.mediaUploadAuth = undefined;
		}, MEDIA_TOKEN_CACHE_TTL_MS); // Cache token for 10 minutes

		return { clientId, token, baseUrl };
	};

	saveAttachment = (fileId: string) => {
		return saveAttachment(this.props.portalId, fileId);
	};

	componentDidMount() {
		if (!this.props.mediaApiUploadToken) {
			this.props.fetchPortalUploadMediaToken(this.props.portalId);
		}
	}

	componentWillUnmount() {
		if (this.timeout) {
			clearTimeout(this.timeout);
		}
	}

	renderError = () => {
		const { id, label } = this.props;
		return <RequestCreateAttachmentFieldError id={id} label={label} />;
	};

	render() {
		const {
			id,
			label,
			description,
			errorMessage,
			mediaApiError,
			required,
			mediaApiUploadToken,
			blockFormSubmission,
		} = this.props;

		if (mediaApiUploadToken) {
			return (
				<UnSupportedBrowserErrorBoundary renderError={this.renderError}>
					<RequestCreateAttachmentFieldDumb
						{...this.props}
						mediaApiUploadToken={mediaApiUploadToken}
						saveAttachment={this.saveAttachment}
						authProvider={this.authProvider}
						blockFormSubmission={blockFormSubmission}
					/>
				</UnSupportedBrowserErrorBoundary>
			);
		}
		return (
			<RequestCreateAttachmentFieldError
				id={id}
				label={label}
				description={description}
				required={required}
				errorMessage={errorMessage}
				mediaApiError={mediaApiError}
			/>
		);
	}
}

const selector = (state: State, props: Props) => {
	const { portalId } = props;
	const mediaApiUploadToken = getPortalMediaApiUploadToken(state, portalId);
	const mediaApiError = getPortalMediaApiError(state, portalId);
	return { ...props, mediaApiUploadToken, mediaApiError };
};
export default connect(selector, {
	fetchPortalUploadMediaToken: fetchPortalUploadMediaTokenAction,
})(RequestCreateAttachmentField);
